<template>
    <div class="row justify-content-center">
        <transition name="fade" mode="out-in">
            <AppPlaceholder class="col-xl-8" v-if="!location" key="no-location"/>
            <div v-else class="col-xl-8" key="location">
                <div class="row">
                    <div class="col-12 mb-4 d-sm-flex justify-content-between align-items-baseline">
                        <div class="pl-3">
                            <h4>
                                {{ isNew ? 'New location' : this.location.name }}
                            </h4>
                            <p class="mb-0 text-muted" v-if="!isNew">
                                <small>Created on {{ location.created_at | date }}</small>
                            </p>
                        </div>
                        <div class="col-12 col-sm-auto text-right pr-0 mt-2 mt-sm-0">
                            <button class="btn btn-primary mr-2" :disabled="loading" @click="save">
                                Save
                            </button>
                            <button class="btn btn-outline-secondary"
                                    :disabled="loading"
                                    @click="saveAndClose">
                                Save & close
                            </button>
                            <b-dropdown variant="link" no-caret right v-if="!isNew">
                                <template v-slot:button-content>
                                    <i class="material-icons">more_vert</i>
                                </template>
                                <b-dropdown-item :disabled="loading" @click="destroy">
                                    Delete
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <p class="col-12 text-muted mb-3"><strong>Address</strong></p>
                        </div>
                        <div class="row">
                            <AppInput
                                    label="Postal code"
                                    :value="location.postal_code"
                                    field="postal_code"
                                    :errors="errors"
                                    @change="updateLocationProps({ postal_code: $event })"
                                    class="col-md-4"
                            />
                            <AppInput
                                    label="City"
                                    :value="location.city"
                                    field="city"
                                    :errors="errors"
                                    @change="updateLocationProps({ city: $event })"
                                    class="col-md-4"
                            />
                            <AppSelect
                                    :value="location.region || null"
                                    :loading="!regions"
                                    :multiple="false"
                                    :options="regions"
                                    track-by="id"
                                    label-field="name"
                                    label="Region"
                                    placeholder=""
                                    @input="updateLocationProps(
                                    { region_id: $event ? $event.id : null })"
                                    class="col-md-4"
                            />
                            <AppSelect
                                    :value="location.country || null"
                                    :loading="!countries"
                                    :multiple="false"
                                    :options="countries"
                                    track-by="code"
                                    label-field="name"
                                    label="Country"
                                    placeholder=""
                                    @input="updateLocationProps(
                                    { country_code: $event ? $event.code : null })"
                                    class="col-md-4"
                            />
                        </div>
                        <div class="row">
                            <AppInput
                                    label="Address"
                                    :value="location.address"
                                    field="Address"
                                    :errors="errors"
                                    @change="updateLocationProps({ address: $event })"
                                    class="col-12"
                            />
                        </div>
                        <SubLocationsList v-if="!isNew"/>
                    </div>
                </div>
                <SubLocationModal :locationModal="locationModal"/>
            </div>
        </transition>
    </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import NotificationService from '../../services/notification.service';
import AppInput from '../form/AppInput';
import AppSelect from '../form/AppSelect';
import AppPlaceholder from '../form/AppPlaceholder';
import Errors from '../../utils/errors';
import { formatDate } from '../../filters/date.filter';
import SubLocationsList from './SubLocationsList';
import SubLocationModal from './SubLocationModal';

export default {
  components: {
    SubLocationModal,
    SubLocationsList,
    AppInput,
    AppPlaceholder,
    AppSelect,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      errors: new Errors(),
      companiesSuggestions: [],
      loading: false,
      canClose: false,
    };
  },

  filters: {
    date: formatDate,
  },
  computed: {
    ...mapGetters({
      location: 'locations/location',
      countries: 'orders/countries',
      regions: 'regions/regions',
    }),
    ...mapState({
      locationModal: (state) => state.locations.locationModal,
    }),
    isNew() {
      return this.locationModal.isNewLocation || this.$route.params.locationId === 'new';
    },
    locationType() {
      if (this.$store.state.locations.locationModal.locationType) {
        return this.$store.state.locations.locationModal.locationType;
      }
      return this.$route.params.locationType;
    },
  },
  mounted() {
    this.initLocation();
  },
  watch: {
    '$route.params.locationId'() {
      this.initLocation();
    },
  },
  methods: {
    async initLocation() {
      if (this.isNew) {
        await this.$store.dispatch('locations/newLocation', this.locationType);
      } else {
        this.getLocation();
      }
    },
    getLocation() {
      this.$store.dispatch(
        'locations/getLocation',
        this.$store.state.locations.locationId
          ? this.$store.state.locations.locationId : this.$route.params.locationId,
      );
    },
    updateLocationProps(props) {
      this.$store.dispatch('locations/updateLocationProps', props);
    },
    saveAndClose() {
      this.save()
        .then(() => {
          if (this.locationModal.isOpen) {
            this.$store.commit('locations/resetLocationModal');
          } else if (this.canClose) {
            this.$router.push({
              locationType: this.locationType,
              name: 'locations',
            });
          }
        });
    },
    save() {
      this.loading = true;
      this.errors.clear();
      return this.$store
        .dispatch(this.isNew ? 'locations/createLocation' : 'locations/updateLocation')
        .then((res) => {
          this.canClose = true;
          NotificationService.success(res.message);
          if (this.locationModal.isOpen) {
            return this.$store.dispatch('locations/getLocation', this.location.id)
              .then((data) => {
                this.$store.commit('locations/locationModal', { isNewLocation: false });
                this.$store.dispatch('locations/newLocationAdded', data);
              });
          }
          if (this.isNew) {
            return this.$router.push({
              name: 'location',
              params: {
                locationType: this.locationType,
                locationId: res.location_id,
              },
            });
          }
          return res;
        })
        .catch((err) => {
          this.canClose = false;
          this.errors.set(err.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    destroy() {
      this.$bvModal.msgBoxConfirm('Delete location?', { okTitle: 'Delete' })
        .then((value) => {
          if (value) {
            this.$store.dispatch('locations/deleteLocation')
              .then((res) => {
                NotificationService.success(res.message);
                if (!this.locationModal.isOpen) {
                  this.$router.push({
                    locationType: this.locationType,
                    name: 'locations',
                  });
                }
                this.$store.commit('locations/resetLocationModal');
              });
          }
        });
    },
  },
};
</script>
